import { includes } from '@hover/utilities';

import type {
  AiDisplayImage,
  AiGeneratedImage,
  AiMaterial,
  AiOriginalImage,
  AiPaintColor,
} from 'src/shared/graphql/generated';

import type {
  SampleDisplayImage,
  SampleGeneratedImage,
  SampleOriginalImage,
} from './components/Home/SampleImages/sampleImages';
import { CreatedFrom } from './static_values';

export type AiProduct = AiPaintColor | AiMaterial;
export type Aspect = 'interior' | 'exterior';

/**
 * AiImage Type Hierarchy:
 *
 * AiImage
 * ├── AiOrSampleOriginalImage
 * │   ├── AiOriginalImage
 * │   └── SampleOriginalImage
 * ├── AiOrSampleGeneratedImage
 * │   ├── AiGeneratedImage
 * │   └── SampleGeneratedImage
 * ├── AiOriginalOrGeneratedImage
 * │   ├── AiOriginalImage
 * │   ├── AiGeneratedImage
 * └── SampleOriginalOrGeneratedImage
 *     ├── SampleOriginalImage
 *     └── SampleGeneratedImage
 *
 * AiImageOrDisplayImage
 * ├── AiImage
 * └── AiOrSampleDisplayImage
 *     ├── AiDisplayImage
 *     └── SampleDisplayImage
 */

export type SampleOriginalOrGeneratedImage =
  | SampleOriginalImage
  | SampleGeneratedImage;
export type AiOriginalOrGeneratedImage = AiOriginalImage | AiGeneratedImage;
export type SampleImage = SampleOriginalOrGeneratedImage | SampleDisplayImage;

export type AiOrSampleOriginalImage = AiOriginalImage | SampleOriginalImage;
export type AiOrSampleGeneratedImage = AiGeneratedImage | SampleGeneratedImage;
export type AiOrSampleDisplayImage = AiDisplayImage | SampleDisplayImage;

export type AiImage = AiOrSampleOriginalImage | AiOrSampleGeneratedImage;

type AiImageOrDisplayImage = AiImage | AiOrSampleDisplayImage;

/**
 * AiImage Type Guards: In addition to determining if a given generic AiImage is a more specific type, with the boolean result,
 * these guards narrow down the type of the image so you can access properties only available on specific types.
 *
 * Example usage:
 *
 * const image: AiImage = {...}
 *
 * const incorrectImagePath = image?.generatedImagePath   //ESLint error: Property 'generatedImagePath' does not exist on type 'AiImage'
 *
 * const correctImagePath = isAiGenerated(image) && image.generatedImagePath  //Valid!
 *
 * We are using these type guards within the homeownerApp module to drive different behavior between user uploaded/generated
 * images and samples (e.g. we do not attempt to generate AI images when viewing samples), and also drive different behavior
 * between original and generated images (e.g. favoriting is restricted to only generated images). For more information on type
 * narrowing and its usage in TypeScript please check out the docs here:
 * @see {@link https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates}
 */

export const isGenerated = (
  image: AiImageOrDisplayImage | undefined | null,
): image is AiOrSampleGeneratedImage =>
  !!image &&
  ('aiOriginalImageId' in image || 'sampleOriginalImageId' in image) &&
  !('createdFrom' in image);

export const isOriginal = (
  image: AiImageOrDisplayImage | undefined | null,
): image is AiOrSampleOriginalImage =>
  !!image &&
  !('aiOriginalImageId' in image || 'sampleOriginalImageId' in image);

export const isDisplay = (
  image: AiImageOrDisplayImage | undefined | null,
): image is AiOrSampleDisplayImage => !!image && 'createdFrom' in image;

export const isSample = (
  image: AiImageOrDisplayImage | undefined | null,
): image is SampleOriginalOrGeneratedImage | SampleDisplayImage =>
  !!image && ('sampleId' in image || 'sampleOriginalImageId' in image);

export const isNotSample = (
  image: AiImageOrDisplayImage | undefined | null,
): image is AiOriginalOrGeneratedImage =>
  !!image && !('sampleId' in image || 'sampleOriginalImageId' in image);

export const isSampleOriginal = (
  image: AiImageOrDisplayImage | undefined | null,
): image is SampleOriginalImage =>
  !!image && isSample(image) && isOriginal(image);

export const isSampleGenerated = (
  image: AiImageOrDisplayImage | undefined | null,
): image is SampleGeneratedImage =>
  !!image && isSample(image) && isGenerated(image);

export const isSampleDisplay = (
  image: AiOrSampleDisplayImage | undefined | null,
): image is SampleDisplayImage => !!image && 'sampleId' in image;

export const isAiDisplay = (
  image: AiOrSampleDisplayImage | undefined | null,
): image is AiDisplayImage => !!image && 'id' in image;

export const isAiOriginal = (
  image: AiImageOrDisplayImage | undefined | null,
): image is AiOriginalImage =>
  !!image && isOriginal(image) && isNotSample(image);

export const isAiGenerated = (
  image: AiImageOrDisplayImage | undefined | null,
): image is AiGeneratedImage =>
  !!image && isGenerated(image) && isNotSample(image);

export const isAiPaintColor = (
  material: AiPaintColor | AiMaterial | undefined | null,
  // eslint-disable-next-line no-underscore-dangle
): material is AiPaintColor => material?.__typename === 'AiPaintColor';

export const isAiMaterial = (
  material: AiPaintColor | AiMaterial | undefined | null,
  // eslint-disable-next-line no-underscore-dangle
): material is AiMaterial => material?.__typename === 'AiMaterial';

/**
 * `getImagePath`: This function returns the appropriate path for the image url
 * based on the type of the image; it always returns a string value.
 *
 * @param {AiImage | undefined | null} image - The image object to retrieve the
 * path from. It can be of type AiImage (or any of its subtypes), undefined, or
 * null. If the image is undefined or null, the function returns an empty string.
 *
 * @returns A string representing the image path. If the image is undefined or null,
 * an empty string is returned.
 *
 */

export const getImagePath = (
  image: AiImageOrDisplayImage | undefined | null,
): string => {
  if (isDisplay(image)) return image.aiGeneratedImage.generatedImagePath;
  if (isOriginal(image)) return image.originalImagePath;
  if (isGenerated(image)) return image.generatedImagePath;

  // FIXME: this can lead to silent failures so we should return undefined and
  // incorporate `undefined` into the return type of the passed image type can
  // be `undefined` or `null`
  return '';
};

/**
 * `GetImageIdType<T>`: This is a utility type that, given an image type `T`,
 * determines the appropriate ID type to be returned by the `getImageId` function.
 * It handles cases where the image could be undefined, null, or a specific type
 * (`SampleOriginalOrGeneratedImage` or `AiOriginalOrGeneratedImage`).
 */

type GetImageIdType<T extends AiImageOrDisplayImage | undefined | null> =
  T extends undefined | null
    ? undefined
    : T extends SampleImage
    ? string
    : number;

/**
 * `getImageId`: This function returns the relevant ID of the given image ("sampleId: string" for sample images
 * or "id: number" for non-sample images). In order to narrow the return value to a number you must
 * pass an image of type AiOriginalOrGeneratedImage; and in order to narrow the return value to a
 * string you must pass an image of type SampleOriginalOrGeneratedImage.
 *
 * @param {AiImage | undefined | null} image - The image object to retrieve the ID from. It can be of
 * type `AiImage`, undefined, or null. If the image is undefined or null, the function returns `undefined`.
 *
 * @returns The relevant ID of the given image. If the image is a sample, the function returns
 * "sampleId: string"; for user images (uploaded or generated), it returns "id: number".
 * If the image is undefined or null, the function returns `undefined`. And finally if the
 * type of the image passed could be undefined or null, the return type of the function can
 * also be undefined.
 */

export const getImageId = <T extends AiImageOrDisplayImage | undefined | null>(
  image: T,
): GetImageIdType<T> => {
  if (isSample(image)) {
    return image.sampleId as GetImageIdType<T>;
  }
  return image?.id as GetImageIdType<T>;
};

export const getFavoriteBool = (
  image: AiOrSampleDisplayImage | AiOrSampleGeneratedImage | undefined | null,
) => {
  if (isDisplay(image)) {
    return !!image.favoritedAt;
  }

  if (isAiGenerated(image)) {
    return image.favorite;
  }

  return false;
};

export const isImageEqual = (
  imageA: AiImageOrDisplayImage,
  imageB: AiImageOrDisplayImage,
): boolean => {
  if (
    ![isOriginal, isGenerated, isDisplay].some(
      guard => guard(imageA) && guard(imageB),
    )
  ) {
    return false;
  }

  return getImageId(imageA) === getImageId(imageB);
};

/**
 * Helper for normalizing an image that might be a display image into its
 * associated generated image, unless an original or generated image is passed.
 * In that case, simply return the provided generated or original image.
 **
 * @param image generated or display image
 *
 * @returns generated image
 */
export const pickImage = <
  T extends
    | AiOrSampleDisplayImage
    | AiOrSampleGeneratedImage
    | undefined
    | null,
>(
  image: T,
) => {
  if (isDisplay(image))
    return image.aiGeneratedImage as T extends SampleDisplayImage
      ? AiOrSampleGeneratedImage
      : AiGeneratedImage;

  return image as T extends undefined
    ? Extract<T, AiImage | undefined>
    : T extends null
    ? Extract<T, AiImage | null>
    : Extract<T, AiImage>;
};

/**
 * Determine whether the provided image is the original image that was uploaded
 * (as opposed to a generated image or customized original image)
 *
 */
export const isMyUpload = (image: AiOrSampleDisplayImage) =>
  image.createdFrom === CreatedFrom.ORIGINAL && !image.aiGeneratedImage.edits;

export interface StylesMetadata {
  [key: string]: {
    style_name: string;
    description: string;
  };
}

export const orientations = ['horizontal', 'vertical'] as const;
export type Orientation = (typeof orientations)[number];

export const isValidOrientation = (
  orientation: string | undefined | null,
): orientation is Orientation => includes(orientations, orientation);
